import { useMemo } from 'react';

import { FormControlLabel, Switch, Typography } from '@material-ui/core';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { MetricEventName } from '@spotify-internal/csr-player';

import { formatPlaybackTime } from '../../utils';
import { EventMarker } from './EventMarker';
import { SpeedMenu } from './SpeedMenu';
import { TimelineSlider } from './TimelineSlider';
import { getRouteLabel, useEventMarkers } from './useEventMarkers';
import { type PlaybackDefaults, usePlaybackState } from './usePlaybackState';

import styles from './SimplifiedPlaybackControls.module.css';

const ROUTE_MARKER_COLOR = 'var(--essential-subdued)';

type SimplifiedPlaybackControlsProps = {
  player: CsrPlayerElement;
  metricEvents?: MetricEvent[];
  playbackDefaults?: PlaybackDefaults;
};

export const SimplifiedPlaybackControls = ({
  player,
  metricEvents = [],
  playbackDefaults,
}: SimplifiedPlaybackControlsProps) => {
  const {
    currentTimeMs,
    totalTimeMs,
    speed,
    skipInactive,
    handleSeek,
    handleSpeedChange,
    handleToggleSkipInactive,
  } = usePlaybackState(player, { defaults: playbackDefaults });

  const routeChangeEvents = useMemo(
    () => metricEvents.filter(e => e.name === MetricEventName.RouteChange),
    [metricEvents],
  );

  const { markers: routeMarkers, handleMarkerClick } = useEventMarkers(
    routeChangeEvents,
    player,
    {
      getColor: () => ROUTE_MARKER_COLOR,
      getLabel: getRouteLabel,
      onSeek: handleSeek,
    },
  );

  return (
    <div className={styles.root}>
      <div className={styles.timelineWrapper}>
        <TimelineSlider
          value={currentTimeMs}
          max={totalTimeMs}
          onChange={handleSeek}
        />
        {routeMarkers.length > 0 && (
          <div className={styles.markersOverlay}>
            {routeMarkers.map((marker, index) => (
              <EventMarker
                key={`${marker.name}-${marker.timestamp}-${index}`}
                marker={marker}
                onClick={handleMarkerClick}
              />
            ))}
          </div>
        )}
      </div>
      <div className={styles.controlsRow}>
        <Typography variant="body2" className={styles.time}>
          {formatPlaybackTime(currentTimeMs)} /{' '}
          {formatPlaybackTime(totalTimeMs)}
        </Typography>
        <FormControlLabel
          className={styles.skipInactiveLabel}
          control={
            <Switch
              size="small"
              checked={skipInactive}
              onChange={(_, checked) => handleToggleSkipInactive(checked)}
              data-track-id="switch.skip-inactive"
              data-track-action="update"
              data-track-data-enabled={!skipInactive}
            />
          }
          label="Skip inactive"
        />
        <SpeedMenu speed={speed} onSpeedChange={handleSpeedChange} />
      </div>
    </div>
  );
};
